import { type ReactNode, useState } from 'react'
import cx from 'classnames'
import { Button } from '@/components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import type { Id } from '@/infra/id'
import { dateFormatters } from '@/utils/date-util'
import { pluralize } from '@/utils/pluralization-util'
import { formatCapacity } from '@/features/user-management/capacity-display'
import styles from './MembershipsSidebar.module.css'

export interface MembershipsSidebarCoupon {
  code: string
  /**
   * The discount, e.g. "$10 OFF".
   */
  value: string
  dateRedeemed: Date
}

export interface MembershipsSidebarSkill {
  id: Id
  name: string
  description: string
}

export interface MembershipsSidebarTeam {
  id: Id
  name: string
  roleName: string
}

export interface MembershipJobTotals {
  /**
   * Number of jobs claimed or idle.
   */
  activeJobs: number
  totalJobs: number
  claimedJobs: number
  onHoldJobs: number
  idleJobs: number
  closedJobs: number
  underConstructionJobs: number
  unclaimedJobs: number
  infoGatheringJobs: number
}

export interface MembershipsSidebarProps {
  workspaceId: Id
  workspaceName: string
  roleName?: string
  dateJoined: Date
  maxConcurrentProviderJobs?: number
  jobTotals: Partial<MembershipJobTotals>
  showCoupons: boolean
  coupons: MembershipsSidebarCoupon[]
  skills: MembershipsSidebarSkill[]
  canEditSkills: boolean
  teams: MembershipsSidebarTeam[]
  onBack: () => void
  onAddToTeam: () => void
  onEditRole: () => void
  onEditSkills: () => void
  onManageCapacity: () => void
  onRemoveFromWorkspace: () => void
}

export function MembershipsSidebar({
  workspaceId,
  workspaceName,
  roleName,
  dateJoined,
  maxConcurrentProviderJobs,
  jobTotals,
  showCoupons,
  coupons,
  skills,
  canEditSkills,
  teams,
  onBack,
  onAddToTeam,
  onEditRole,
  onEditSkills,
  onManageCapacity,
  onRemoveFromWorkspace,
}: MembershipsSidebarProps) {
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <IconButton name="back" label="Back" onPress={onBack} size={24} />
        </div>
        <Dropdown
          autoDismiss
          position="bottom-end"
          button={
            <IconButton
              name="more"
              label="Membership actions"
              testId={`memberships-sidebar-actions-${workspaceId}`}
              size={24}
            />
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem
            onPress={onAddToTeam}
            buttonTestId="memberships-sidebar-actions-add-to-team"
          >
            Add user to team
          </DropdownItem>
          <DropdownItem
            onPress={onEditRole}
            buttonTestId={`memberships-sidebar-actions-edit-role-${workspaceId}`}
          >
            Edit role
          </DropdownItem>
          <DropdownItem
            onPress={onEditSkills}
            buttonTestId={`memberships-sidebar-actions-edit-skills-${workspaceId}`}
          >
            Edit skills
          </DropdownItem>
          <DropdownItem
            onPress={onManageCapacity}
            buttonTestId={`memberships-sidebar-actions-manage-capacity-${workspaceId}`}
          >
            Manage capacity
          </DropdownItem>
          <DropdownItem
            onPress={onRemoveFromWorkspace}
            buttonTestId={`memberships-sidebar-actions-remove-from-workspace-${workspaceId}`}
          >
            Remove from workspace
          </DropdownItem>
        </Dropdown>
      </div>
      <div className={styles.membershipInfo}>
        <h2 className={styles.membershipInfoTitle}>{workspaceName}</h2>
        <div className={styles.membershipInfoGrid}>
          <MembershipInfoGridRow title="Role" value={roleName} />
          <MembershipInfoGridRow
            title="Joined"
            value={dateFormatters.dateLongWithTime.format(dateJoined)}
          />
          <MembershipInfoGridRow
            title="Active jobs"
            value={`${jobTotals.activeJobs} / ${formatCapacity(
              maxConcurrentProviderJobs,
            )}`}
          />
          <TotalJobsGridRow jobTotals={jobTotals} />
        </div>
      </div>
      <div className={styles.membershipItems}>
        {showCoupons && (
          <Accordion
            title="Coupons redeemed"
            items={coupons}
            renderItem={(userCoupon) => (
              <AccordionItem
                key={userCoupon.dateRedeemed.toUTCString()}
                title={userCoupon.code}
                addendum={userCoupon.value}
                subtitle={`Redeemed ${dateFormatters.dateTimeDefault.format(userCoupon.dateRedeemed)}`}
              />
            )}
          />
        )}
        <Accordion
          title="Skills"
          items={skills}
          renderItem={(skill) => (
            <AccordionItem
              key={skill.id}
              title={skill.name}
              subtitle={skill.description}
              testId={`memberships-sidebar-skill-${skill.id}`}
            />
          )}
          buttonText={canEditSkills ? 'Edit' : ''}
          buttonAction={onEditSkills}
          testId="memberships-sidebar-manage-skills"
        />
        <Accordion
          title={`${pluralize('Team', teams.length)}`}
          items={teams}
          renderItem={(team) => (
            <AccordionItem
              key={team.id}
              title={team.name}
              subtitle={team.roleName}
              testId={`memberships-sidebar-team-${team.id}`}
            />
          )}
        />
      </div>
    </div>
  )
}

interface MembershipInfoGridRowProps {
  title: string
  value?: string | number
}

function MembershipInfoGridRow({ title, value }: MembershipInfoGridRowProps) {
  return (
    <>
      <div className={styles.membershipInfoGridTitle}>{title}</div>
      <div className={styles.membershipInfoGridValue}>{value}</div>
    </>
  )
}

interface AccordionProps<T> {
  title: string
  items: T[]
  renderItem: (item: T, index: number) => ReactNode
  buttonText?: string
  buttonAction?: () => void
  testId?: string
}

function Accordion<T>({
  title,
  items,
  renderItem,
  buttonText,
  buttonAction,
  testId,
}: AccordionProps<T>) {
  const [isExtended, setIsExtended] = useState(true)

  const toggleIsExtended = () => {
    setIsExtended(!isExtended)
  }

  return (
    <div className={styles.accordion} data-test={testId}>
      <div className={styles.accordionHeader}>
        <IconButton
          name="arrow"
          label={isExtended ? 'Collapse' : 'Expand'}
          className={cx(
            styles.accordionHeaderIcon,
            !isExtended && styles.rotate,
          )}
          onPress={toggleIsExtended}
          size={24}
        />
        <div className={styles.accordionHeaderText}>
          <span className={styles.accordionHeaderTitle}>
            {title}
            <span className={styles.accordionHeaderCount}>
              {`(${items.length})`}
            </span>
          </span>
        </div>
        {buttonText && buttonAction && (
          <Button
            label={buttonText}
            className={styles.accordionAction}
            variant="text"
            onPress={buttonAction}
          />
        )}
      </div>
      <div
        className={cx(styles.accordionContent, !isExtended && styles.contract)}
      >
        {items.map((item, index) => renderItem(item, index))}
      </div>
    </div>
  )
}

interface AccordionItemProps {
  title: string
  addendum?: string
  subtitle?: string
  testId?: string
}

function AccordionItem({
  title,
  addendum,
  subtitle,
  testId,
}: AccordionItemProps) {
  return (
    <div className={styles.accordionItem} data-test={testId}>
      <div className={styles.accordionItemText}>
        <div className={styles.accordionItemTitle}>
          <h5>{title}</h5>
          {addendum && (
            <>
              <span className={styles.accordionItemDivider}>•</span>
              <span className={styles.accordionItemAddendum}>{addendum}</span>
            </>
          )}
        </div>
        {subtitle && (
          <div className={styles.accordionItemSubtitle}>{subtitle}</div>
        )}
      </div>
    </div>
  )
}

interface TotalJobsGridRowProps {
  jobTotals: Partial<MembershipJobTotals>
}

function TotalJobsGridRow({ jobTotals }: TotalJobsGridRowProps) {
  return (
    <>
      <div className={styles.membershipInfoGridTitle}>Total jobs</div>
      <div className={styles.membershipTotalJobs}>
        {jobTotals.totalJobs}
        {!!jobTotals.totalJobs && (
          <Dropdown
            width="miniscule"
            autoDismiss
            button={
              <IconButton
                name="dropdown"
                label="Show job totals"
                size={14}
                color="var(--grey600)"
              />
            }
          >
            <DropdownTitle>Total jobs</DropdownTitle>

            {!!jobTotals.infoGatheringJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Info gathering"
                  value={jobTotals.infoGatheringJobs}
                />
              </DropdownItem>
            )}

            {!!jobTotals.unclaimedJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Unclaimed"
                  value={jobTotals.unclaimedJobs}
                />
              </DropdownItem>
            )}

            {!!jobTotals.claimedJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="In progress"
                  value={jobTotals.claimedJobs}
                />
              </DropdownItem>
            )}

            {!!jobTotals.idleJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Idle"
                  value={jobTotals.idleJobs}
                />
              </DropdownItem>
            )}

            {!!jobTotals.onHoldJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="On hold"
                  value={jobTotals.onHoldJobs}
                />
              </DropdownItem>
            )}

            {!!jobTotals.closedJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Completed"
                  value={jobTotals.closedJobs}
                />
              </DropdownItem>
            )}

            {!!jobTotals.underConstructionJobs && (
              <DropdownItem muteClickEvent>
                <MembershipInfoGridRow
                  title="Under construction"
                  value={jobTotals.underConstructionJobs}
                />
              </DropdownItem>
            )}
          </Dropdown>
        )}
      </div>
    </>
  )
}
